// Packages
import i18n, { TOptions } from 'i18next';
// Modules
import webStorage from 'modules/storage';
// I18N
import en from './dictionaries/en.json';
import enUsOptions from './locales/en.json';
// Locale
import enUS from 'antd/lib/locale/en_US';
import { Locale } from 'antd/lib/locale';

enum LANG {
  EN = 'en',
  ES = 'es',
  VI = 'vi',
}

export const languages = {
  [LANG.EN]: [LANG.EN],
  [LANG.ES]: [LANG.ES],
  [LANG.VI]: [LANG.VI],
} as const;

interface ILanguageBundle {
  dictionary: object;
  localeOptions: object;
  antdLocale: Locale;
}

// English is bundled: it is the fallback for every other language and the default one. The others
// are separate chunks (~35 KB gzip each), filled in by the top-level await below.
const bundles: Partial<Record<LANG, ILanguageBundle>> = {
  [LANG.EN]: { dictionary: en, localeOptions: enUsOptions, antdLocale: enUS },
};

const loaders: Partial<Record<LANG, () => Promise<{ default: ILanguageBundle }>>> = {
  [LANG.ES]: () => import('./languages/es'),
  [LANG.VI]: () => import('./languages/vi'),
};

const englishBundle = bundles[LANG.EN] as ILanguageBundle;

const getBundle = (language: TLangKeys) => bundles[language] ?? englishBundle;

export type TLangKeys = keyof typeof languages;

export type TLang = { lng: TLangKeys };

const getStoredLanguage = (): TLangKeys =>
  webStorage.getData<TLang>('timeGoLang')?.lng || languages.en;

// Awaited before this module counts as evaluated, so no importer renders (or calls t() at its own
// top level) before the language is there: no flash of English or of keys. A failed load (offline,
// blocked chunk) leaves the app in English instead of blank.
const loadStoredLanguage = async () => {
  const language = getStoredLanguage();

  try {
    const bundle = await loaders[language]?.();

    if (bundle) {
      bundles[language] = bundle.default;
    }
  } catch {
    // English stays in use
  }
};

await loadStoredLanguage();

const activeLanguage = getStoredLanguage() in bundles ? String(getStoredLanguage()) : LANG.EN;

i18n.init({
  // Add another translations here
  // and to /i18next-parser.config.js
  resources: {
    [LANG.EN]: { translation: englishBundle.dictionary },
    [activeLanguage]: { translation: getBundle(activeLanguage as TLangKeys).dictionary },
  },
  lng: activeLanguage,
  fallbackLng: languages.en,
  interpolation: {
    escapeValue: false,
  },
});

// USAGE:
// import { t } from '../i18n'
// const translated = t('Some en text');
export const langs = Object.keys(languages) as TLangKeys[];

// i18next 21 infers `t`'s result from the call site; inside JSX that falls back to its wide
// `string | object | null` result, which React 18+ types no longer accept as a node. Every
// dictionary entry is a plain string and nothing asks for `returnObjects`, so say so here.
type TTranslate = (key: string | string[], options?: TOptions | string) => string;

export const t: TTranslate = (key, options) => i18n.t(key, options);

export const changeLanguage = (lng: TLangKeys): void => {
  webStorage.setData<TLang>({ lng }, 'timeGoLang');
  window.location.reload();
};

export const getCurrentLanguage = getStoredLanguage;

export const getCurrentLocale = (): Locale => {
  const currentLanguage = getCurrentLanguage();

  return getBundle(currentLanguage).antdLocale;
};

export const getLocaleOptions = (startDay: number) => {
  const currentLanguage = getCurrentLanguage();

  const { localeOptions } = getBundle(currentLanguage);

  return { ...localeOptions, week: { dow: startDay } };
};

export default i18n;
